// base
import { useMemo, useState } from 'react';

// hooks
import { useLoading } from './useLoading';

// modules
import type { ResultError, CustomMutationOptions } from 'modules';

// constants
import { customMutationInitialOptions } from 'constants/const';

// libraries
import {
  type QueryKey,
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient
} from '@tanstack/react-query';

/**
 * 공통 Mutation 훅의 옵션을 정의하는 인터페이스입니다.
 *
 * @template TData - Mutation 함수가 반환하는 데이터의 타입
 * @template TError - 에러의 타입
 * @template TVariables - Mutation 함수에 전달되는 변수의 타입
 * @template TContext - 컨텍스트의 타입
 */
export interface UseCommonMutationOptions<TData, TError, TVariables, TContext>
  extends Omit<
    UseMutationOptions<TData, TError, MutationParams<TData, TVariables>, TContext>,
    'mutationFn'
  > {
  /** Mutation 키로, 캐싱과 식별에 사용됩니다. */
  mutationKey: QueryKey;
  /** 실제로 데이터를 Mutation하는 함수입니다. */
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Mutation 후 무효화할 쿼리 키입니다. */
  queryKeyToInvalidate?: QueryKey;
  /** Mutation 커스텀 옵션 */
  customOptions?: CustomMutationOptions<TData, ResultError, TVariables>;
}

/** 공통 Mutation 훅의 커스텀 옵션을 받는 인터페이스입니다. */
export interface MutationParams<TData, TVariables> {
  /** Mutation 할 데이터 */
  data: TVariables;
  /** Mutation 할때의 커스텀 옵션 */
  options?: CustomMutationOptions<TData, ResultError, TVariables>;
}

/**
 * 공통 Mutation 훅으로, Mutation 로직을 재사용 가능하게 만듭니다.
 *
 * @template TData - Mutation 함수가 반환하는 데이터의 타입
 * @template TError - 에러의 타입
 * @template TVariables - Mutation 함수에 전달되는 변수의 타입
 * @template TContext - 컨텍스트의 타입
 *
 * @param {UseCommonMutationOptions<TData, TError, TVariables, TContext>} options - Mutation 훅에 필요한 옵션들
 * @returns {UseMutationResult<TData, TError, TVariables, TContext>} Mutation 훅의 결과
 *
 * @example
 * const mutation = useCommonMutation({
 *   mutationKey: ['createUser'],
 *   mutationFn: (data) => api.createUser(data),
 *   queryKeyToInvalidate: ['users'],
 *   onSuccess: () => {
 *     // 성공 시 실행할 추가 로직
 *   },
 * });
 */
export const useCommonMutation = <
  TData extends Pick<ResultError, 'message'>,
  TError extends ResultError,
  TVariables
>({
  mutationKey,
  mutationFn,
  queryKeyToInvalidate,
  customOptions = { ...customMutationInitialOptions },
  ...options
}: UseCommonMutationOptions<TData, TError, TVariables, unknown>): UseMutationResult<
  TData,
  TError,
  MutationParams<TData, TVariables>,
  unknown
> => {
  const [runtimeOptions, setRuntimeOptions] =
    useState<CustomMutationOptions<TData, TError, TVariables>>();

  /** React Query의 QueryClient를 가져옵니다. */
  const queryClient = useQueryClient();

  /** Toast 컨텍스트 훅을 가져옵니다. */
  // const { showToast, isNotProvided } = useToastHook();

  /** Loading 컨텍스트 훅을 가져옵니다. */
  const { setLoading, isNotProvided: isNotLoadingProvided } = useLoading();

  const mergedOptions = useMemo(() => {
    const result = {
      ...customMutationInitialOptions,
      ...customOptions,
      ...runtimeOptions
    };

    return result;
  }, [customOptions, runtimeOptions]);

  /** Mutation 훅을 사용하여 Mutation 로직을 처리합니다. */
  const mutationResult = useMutation<TData, TError, MutationParams<TData, TVariables>, unknown>({
    mutationKey,
    mutationFn: async ({ data, options }) => {
      if (options) setRuntimeOptions(options);
      return await mutationFn(data);
    },
    /** Mutation 실행 전 실행되는 함수로, 낙관적 업데이트 등을 처리합니다. */
    onMutate: async (params) => {
      /** 실행 전 로딩상태 여부를 변경합니다. */
      if (!isNotLoadingProvided && !mergedOptions.disableLoading) {
        setLoading(true);
      }

      /** 이전 데이터를 가져옵니다. */
      const previousData = queryClient.getQueryData<TData>([mutationKey, params]);
      /** 해당 쿼리를 취소합니다. */
      await queryClient.cancelQueries({ queryKey: [mutationKey, params] });

      /** 사용자 정의 onMutate가 있다면 실행합니다. */
      if (options.onMutate) {
        return options.onMutate(params);
      }

      /** 실행 전 동작이 있다면 실행합니다. */
      if (mergedOptions.beforeMutation) {
        mergedOptions.beforeMutation(params);
      }

      /** 이전 데이터를 컨텍스트로 반환합니다. */
      return { previousData };
    },
    /** Mutation 성공 시 실행되는 함수입니다. */
    onSuccess: (...args) => {
      /** Mutation 성공 시 토스트 메시지를 띄웁니다. */
      // if (!isNotProvided && !mergedOptions.disableToast) {
      //   showToast({
      //     message: mergedOptions.successMessage || args[0].message || ''
      //   });
      // }

      /** 지정된 쿼리 키를 무효화합니다. */
      if (queryKeyToInvalidate && mergedOptions.invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
      }

      /** 사용자 정의 onSuccess가 있다면 실행합니다. */
      if (options.onSuccess) {
        options.onSuccess(...args);
      }

      /** 성공 후 추가 동작이 있다면 실행합니다. */
      if (mergedOptions.afterSuccess) {
        mergedOptions.afterSuccess(args[0]);
      }
    },
    /** Mutation 실패 시 실행되는 함수입니다. */
    onError: (error, variables, context) => {
      /** Mutation 실패 시 토스트 메시지를 띄웁니다. */
      // if (!isNotProvided && !mergedOptions.disableToast) {
      //   showToast({
      //     message: mergedOptions.errorMessage || error.message || ''
      //   });
      // }

      /** 낙관적 업데이트를 롤백합니다. */
      if (context && (context as any).previousData && mergedOptions.rollbackOptimistic) {
        queryClient.setQueryData([mutationKey, variables], (context as any).previousData);
      }

      /** 사용자 정의 onError가 있다면 실행합니다. */
      if (options.onError) {
        options.onError(error, variables, context);
      }

      /** 에러 후 추가 동작이 있다면 실행합니다. */
      if (mergedOptions.afterError) {
        mergedOptions.afterError(error);
      }
    },
    /** Mutation 완료 후 실행되는 함수입니다. */
    onSettled: (...args) => {
      /** 실행 후 로딩상태 여부를 변경합니다. */
      if (!isNotLoadingProvided && !mergedOptions.disableLoading) {
        setLoading(false);
      }

      /** 지정된 쿼리 키를 무효화합니다. */
      if (queryKeyToInvalidate && mergedOptions.invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
      }

      /** 사용자 정의 onSettled가 있다면 실행합니다. */
      if (options.onSettled) {
        options.onSettled(...args);
      }

      /** 공통 추가 동작이 있다면 실행합니다. */
      if (mergedOptions.commonCallback) {
        mergedOptions.commonCallback({
          success: args[0],
          error: args[1],
          variables: args[2],
          context: args[3]
        });
      }
    },
    /** 기타 옵션들을 전달합니다. */
    ...options
  });

  return {
    ...mutationResult
  };
};
